import { 
  useCallback,
  useEffect, 
  useRef,
  useState
} from "react";

import { 
  deleteObject, 
  getDownloadURL, 
  uploadBytes, 
  UploadResult 
} from "firebase/storage";
import { ref } from "firebase/storage";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Stack from "@mui/material/Stack";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";

import { storage } from "../../firebase";


interface GratiImageUploadProps {
  onImageUpdated?: (snapshot: UploadResult | null) => void;  // for immediate upload mode
  onFileSelected?: (file: File | null) => void;              // for deferred upload mode
  deferUpload?: boolean;                                      // if true, don't upload, just call onFileSelected
  size?: "small" | "medium" | "large";
  isInitialized?: boolean;
  storageImageFileName: string; // store the image using this file name
  storageImageLocation: string; // store at this location;
  label?: string;
  isCompact?: boolean;  // if true, show compact view with overlay icons
}

export default function GratiImageUpload(props: GratiImageUploadProps) {
  const { onImageUpdated, onFileSelected, storageImageFileName, storageImageLocation } = props;
  const deferUpload = props.deferUpload ?? false;
  const isInitialized = props.isInitialized ?? false;
  const isCompact = props.isCompact ?? false;
  const label = props.label ? props.label : "Upload Image";
  const size = props.size ? props.size : "medium";
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const storageImageRef = ref(storage, storageImageLocation);

  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [showCompactActions, setShowCompactActions] = useState(false);

  useEffect(() => {
    console.log("isInitialized: ", isInitialized);
    if (isInitialized) {
      getImage(storageImageFileName).then(() => {
        console.log('Image loaded from storage');
      }).catch((error) => {
        console.error('Error getting image on mount', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, storageImageFileName]);

  const getImage = useCallback(async (imageFileName: string) => {
    if (storageImageRef) {
      const logoRef = ref(storageImageRef, imageFileName);
      console.log("logoRef: ", logoRef);
      try {
        const url = await getDownloadURL(logoRef);
        setImageUrl(url);
      } catch (error) {
        console.error('Error getting image:', error);
      setImageUrl(null);
      }
    } else {
      setImageUrl(null);
    }
  }, [storageImageRef]);

  const deleteImage = function () {
    if (deferUpload) {
      // Deferred mode: just clear local state, no storage operation
      setImageUrl(null);
      onFileSelected && onFileSelected(null);
    } else {
      // Immediate mode: delete from storage
      const logoRef = ref(storageImageRef, storageImageFileName);
      deleteObject(logoRef).then(() => {
        setImageUrl(null);
        onImageUpdated && onImageUpdated(null);
      }).catch((error) => {
        console.error('Error deleting image:', error);
      });
    }
  };

  const uploadImage = function (newLogo: File) {
    const logoRef = ref(storageImageRef, storageImageFileName);
    uploadBytes(logoRef, newLogo).then((snapshot) => {
      console.log("uploaded: ", snapshot);
      onImageUpdated && onImageUpdated(snapshot);
      getImage(storageImageFileName).catch((error) => {
        console.error('Error getting image after upload', error);
      });
      console.log('Uploaded a blob or file!');
    }).catch((error) => {
      console.error('Error uploading image:', error);
    });
  };

  const avatarSize = size === "small" ? 40 : size === "medium" ? 60 : 80;

  const fileInput = (
    <input
      ref={uploadInputRef}
      type="file"
      accept="image/*"
      aria-label="Upload image"
      style={{ display: 'none' }}
      onChange={(event) => {
        if (event.target.files && event.target.files.length > 0) {
          const file = event.target.files[0];
          if (deferUpload) {
            // Deferred mode: create local preview, pass file to parent
            setImageUrl(URL.createObjectURL(file));
            onFileSelected && onFileSelected(file);
          } else {
            // Immediate mode: upload to storage now
            uploadImage(file);
          }
          setShowCompactActions(false);
        }
      }}
    />
  );

  // Compact view
  if (isCompact) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
        {fileInput}
        <Box sx={{ position: 'relative', display: 'inline-flex', width: avatarSize, height: avatarSize }}>
          {imageUrl ? (
            // Has image: show image with edit overlay
            (<>
              <Avatar
                src={imageUrl}
                sx={{ width: avatarSize, height: avatarSize }}
              />
              {/* Edit button overlay */}
              {!showCompactActions && (
                <IconButton
                  aria-label="Edit image"
                  size="small"
                  onClick={() => setShowCompactActions(true)}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 20,
                    height: 20,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}
            </>)
          ) : (
            // No image: show placeholder with add icon
            (<IconButton
              aria-label="Add image"
              onClick={() => uploadInputRef.current?.click()}
              sx={{
                width: avatarSize,
                height: avatarSize,
                bgcolor: 'action.hover',
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: '50%',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              }}
            >
              <ImageIcon sx={{ color: 'text.secondary' }} />
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon sx={{ fontSize: 12 }} />
              </Box>
            </IconButton>)
          )}
        </Box>
        {/* Action buttons that expand to the right */}
        {showCompactActions && (
          <Slide direction="left" in={showCompactActions} mountOnEnter unmountOnExit>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              {!cancelConfirm ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => uploadInputRef.current?.click()}
                    startIcon={<ImageIcon />}
                  >
                    Change
                  </Button>
                  <IconButton
                    aria-label="Delete image"
                    size="small"
                    onClick={() => setCancelConfirm(true)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Close"
                    size="small"
                    onClick={() => setShowCompactActions(false)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    onClick={() => {
                      deleteImage();
                      setCancelConfirm(false);
                      setShowCompactActions(false);
                    }}
                  >
                    Confirm delete
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    onClick={() => setCancelConfirm(false)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Stack>
          </Slide>
        )}
      </Stack>
    );
  }

  // Standard view (default)
  return (
    <Stack useFlexGap spacing={2} direction="row" sx={{ alignItems: 'center' }}>
      {imageUrl &&
        <Avatar src={imageUrl} sx={{ width: avatarSize, height: avatarSize }} />
      }
      {fileInput}
      <Button
        onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
        variant="outlined"
        size="small"
        color="primary"
        startIcon={<ImageIcon />}
      >
        {imageUrl ? "Change" : "Add " + label}
      </Button>
      {imageUrl && !cancelConfirm &&
        <Button
          onClick={() => setCancelConfirm(true)}
          variant="text"
          color="secondary"
          startIcon={<DeleteIcon />}
        >
          Delete
        </Button>
      }
      {cancelConfirm &&
        <Stack direction="row" spacing={1}>
          <Button variant="text" size="small" color="secondary" onClick={() => {
              deleteImage();
              setCancelConfirm(false);
          }}
          >
            Confirm delete
          </Button>
          <Button variant="text" size="small" color="secondary" onClick={() => setCancelConfirm(false)}>
            Undo delete
          </Button>
        </Stack>
      }
    </Stack>
  );
}
